// koerperschaftBegriff.ts — Beschriftungen der Firmenmaske nach Rechtsform.
//
// Ein Verein hat keinen „Firmennamen" und keinen „Unternehmenssitz" (PO
// 19.08.2026). Welche Körperschaft vorliegt, sagt dasselbe Stammdatum, das
// schon die Verbindungsliste benennt: `ContactLegalForm.verbindungLabel`
// (siehe utils/unternehmensBegriff.ts) — „Unternehmen" (Default), „Verein",
// „Stiftung", „Genossenschaft", „Gemeinschaft" oder „Körperschaft".
//
// Die Texte hängen damit an der GEWÄHLTEN Rechtsform, nicht am Einstieg ins
// Formular: Wer in der normalen Firmen-Anlage auf „Eingetragener Verein
// (e.V.)" umstellt, sieht die Beschriftung sofort wechseln. Der Menüpunkt
// „Verein, Stiftung, …" (utils/kontaktAnlageTypen.ts) ist nur der Sonderfall,
// in dem die Rechtsform schon beim Öffnen steht.
//
// ⚠️ Hier entstehen ausschließlich ANZEIGETEXTE und Sichtbarkeits-Hinweise
// (`art`, `hatRegister`) — Feldnamen, Spalten und gespeicherte Werte bleiben
// unberührt. Ein ausgeblendetes Feld verliert seinen Wert nicht.

/**
 * Welche Körperschaft vorliegt — abgeleitet aus DEMSELBEN `verbindungLabel`,
 * das auch die Texte bestimmt. Sie reist mit den Texten mit, damit eine Maske,
 * die nicht nur anders beschriften, sondern anders AUSSEHEN muss (Verein und
 * Stiftung führen Gemeinnützigkeit, eine GmbH nicht), keine zweite Ableitung
 * bauen muss.
 */
export type KoerperschaftArt =
  | "unternehmen"
  | "verein"
  | "stiftung"
  | "genossenschaft"
  | "gemeinschaft";

/**
 * Begriffe der Branchenauswahl (`BrancheZweiSelect`). Ein Verein hat keine
 * „Branche", sondern eine Ausrichtung mit Schwerpunkten (User 01.10.2026) —
 * Katalog, Speicherform und Bedienung bleiben dieselben, nur die Wörter
 * wechseln.
 */
export type BrancheBegriffe = {
  /** Beschriftung des Ebene-1-Feldes. */
  ebene1Label: string;
  /** Beschriftung des Ebene-2-Feldes (Mehrfachauswahl). */
  ebene2Label: string;
  /** Link unter den Blöcken, der einen weiteren Block anhängt. */
  weitereHinzufuegen: string;
  /** Kopfzeile eines noch leeren weiteren Blocks. */
  weitererBlockTitel: string;
  /** Aktion „Block entfernen", wenn mehrere Blöcke stehen. */
  blockEntfernen: string;
  /** Aktion „Block zurücksetzen" beim einzigen Block. */
  blockZuruecksetzen: string;
  /** Hinweis im Ebene-2-Feld, solange Ebene 1 fehlt. */
  zuerstEbene1: string;
  /** Leertext der Ebene-1-Suche. */
  ebene1NichtGefunden: string;
  /** Leertext und Suchfeld-Beschriftung der Ebene-2-Liste. */
  ebene2NichtGefunden: string;
  ebene2Suche: string;
  /** Warnung unter einem Block mit Ebene 1, aber ohne Ebene 2. */
  ohneEbene2: string;
  /** Beschriftung im Lesemodus. */
  leseLabel: string;
  /** Lesemodus ohne Auswahl. */
  leer: string;
  /** Rückfrage beim Wechsel der Ebene 1, wenn schon Ebene-2-Einträge stehen. */
  wechselTitel: string;
  wechselText: string;
  wechselJa: string;
  /** Titel des Akte-Abschnitts, in dem die Auswahl steht. */
  abschnittTitel: string;
};

/** Die heutigen Branchen-Begriffe — für alle Rechtsformen außer dem Verein. */
export const BRANCHE_BEGRIFFE_FIRMA: BrancheBegriffe = {
  ebene1Label: "Branche",
  ebene2Label: "Branchengruppen",
  weitereHinzufuegen: "+ weitere Branche",
  weitererBlockTitel: "Weitere Branche",
  blockEntfernen: "Branche entfernen",
  blockZuruecksetzen: "Branche zurücksetzen",
  zuerstEbene1: "Bitte zuerst eine Branche wählen.",
  ebene1NichtGefunden: "Keine Branche gefunden",
  ebene2NichtGefunden: "Keine Branchengruppe gefunden.",
  ebene2Suche: "Branchengruppen durchsuchen",
  ohneEbene2: "Bitte Branchengruppe wählen — ohne sie wird diese Branche nicht gespeichert.",
  leseLabel: "Branche (Klassifikation)",
  leer: "Keine Branche klassifiziert.",
  wechselTitel: "Branche wechseln?",
  wechselText:
    "Die gewählten Branchengruppen gehören zur bisherigen Branche und werden dabei entfernt.",
  wechselJa: "Branche wechseln",
  abschnittTitel: "Branche und Tätigkeit",
};

/** Verein: „Ausrichtung und Schwerpunkt" statt Branche (User 01.10.2026). */
export const BRANCHE_BEGRIFFE_VEREIN: BrancheBegriffe = {
  ebene1Label: "Ausrichtung und Schwerpunkt",
  ebene2Label: "Schwerpunkte",
  weitereHinzufuegen: "+ weiterer Schwerpunkt",
  weitererBlockTitel: "Weiterer Schwerpunkt",
  blockEntfernen: "Schwerpunkt entfernen",
  blockZuruecksetzen: "Schwerpunkt zurücksetzen",
  zuerstEbene1: "Bitte zuerst eine Ausrichtung wählen.",
  ebene1NichtGefunden: "Keine Ausrichtung gefunden",
  ebene2NichtGefunden: "Kein Schwerpunkt gefunden.",
  ebene2Suche: "Schwerpunkte durchsuchen",
  ohneEbene2:
    "Bitte Schwerpunkt wählen — ohne ihn wird diese Ausrichtung nicht gespeichert.",
  leseLabel: "Ausrichtung und Schwerpunkt",
  leer: "Keine Ausrichtung erfasst.",
  wechselTitel: "Ausrichtung wechseln?",
  wechselText:
    "Die gewählten Schwerpunkte gehören zur bisherigen Ausrichtung und werden dabei entfernt.",
  wechselJa: "Ausrichtung wechseln",
  abschnittTitel: "Ausrichtung und Schwerpunkt",
};

/** Beschriftungen einer Anlage-/Detailmaske für eine Körperschaftsform. */
export type KoerperschaftTexte = {
  /** Körperschaftsform als Unterscheidungsmerkmal (siehe `KoerperschaftArt`). */
  art: KoerperschaftArt;
  /** Seitentitel der Anlage-Maske. */
  seitenTitel: string;
  /** Überschrift der Stammdaten-Karte. */
  kartenUeberschrift: string;
  /** Beschriftung des Namensfeldes — in Anlage und Kundenakte. */
  nameLabel: string;
  /** Platzhalter des Namensfeldes. */
  namePlatzhalter: string;
  /** Titel der Anschrift-Karte. */
  anschriftTitel: string;
  /** Beschriftung des Registernummer-Feldes (Steuer- & Rechtsdaten). */
  registerNummerLabel: string;
  /** Beschriftung der Nur-Lesen-Zeile „Amtsgericht <Ort> <Nummer>". */
  registerLabel: string;
  /** Platzhalter der Registernummer — die Registerart gehört mit in die Nummer. */
  registerPlatzhalter: string;
  /**
   * `false` = für diese Körperschaft gibt es kein Register, auf das die Maske
   * verweisen könnte. Betrifft ausschließlich die Stiftung: sie steht im
   * Stiftungsverzeichnis ihres Bundeslandes, und die 16 Landesverzeichnisse
   * bildet das System nicht ab. Ein leeres „Handelsregister" an einer Stiftung
   * fragte nach einer Nummer, die es nicht gibt.
   */
  hatRegister: boolean;
  /** Begriffe der Branchenauswahl — beim Verein „Ausrichtung und Schwerpunkt". */
  branche: BrancheBegriffe;
};

/** Die heutigen Firmen-Texte — zugleich der Fallback. */
export const KOERPERSCHAFT_FIRMA: KoerperschaftTexte = {
  art: "unternehmen",
  seitenTitel: "Firmenkontakt anlegen",
  kartenUeberschrift: "Firmenangaben",
  nameLabel: "Name des Unternehmens",
  namePlatzhalter: "Muster GmbH",
  anschriftTitel: "Anschrift (Unternehmenssitz)",
  registerNummerLabel: "Handelsregisternummer",
  registerLabel: "Handelsregister",
  registerPlatzhalter: "z. B. HRB 12345",
  hatRegister: true,
  branche: BRANCHE_BEGRIFFE_FIRMA,
};

/**
 * Eine Tabelle statt einer Ableitung, weil sich die Formen nicht regelhaft
 * bilden lassen: „Name des Vereins" steht im Genitiv Maskulinum, „Name der
 * Stiftung" im Femininum; „Vereinsangaben" trägt ein Fugen-s, während
 * „Genossenschaftsangaben" niemand sagt („Angaben zur Genossenschaft").
 *
 * Schlüssel ist der klein geschriebene `verbindungLabel`-Wert. „Körperschaft"
 * fehlt bewusst: die Anlage einer Körperschaft des öffentlichen Rechts steht
 * hier nicht zur Debatte, und geratene Texte wären schlechter als die
 * neutralen Firmen-Texte.
 *
 * Die Registerbegriffe folgen derselben Logik: Vereine stehen im
 * Vereinsregister (VR), Genossenschaften im Genossenschaftsregister (GnR) —
 * beide beim Amtsgericht, also bleibt das Gerichtsfeld daneben richtig. Die
 * Gemeinschaft behält die Handelsregister-Texte: die Eigentümergemeinschaften
 * im Bestand tragen dort ohnehin nichts ein. Die eGbR steht seit 2024 im
 * Gesellschaftsregister desselben Amtsgerichts — sie hängt nicht an diesem
 * Begriff, sondern an der Rechtsform selbst (siehe `GESELLSCHAFTSREGISTER`).
 */
const NACH_BEGRIFF: Record<string, KoerperschaftTexte> = {
  verein: {
    art: "verein",
    seitenTitel: "Verein anlegen",
    kartenUeberschrift: "Vereinsangaben",
    nameLabel: "Name des Vereins",
    namePlatzhalter: "Musterverein e.V.",
    anschriftTitel: "Anschrift (Sitz)",
    registerNummerLabel: "Vereinsregisternummer",
    registerLabel: "Vereinsregister",
    registerPlatzhalter: "z. B. VR 1234",
    hatRegister: true,
    branche: BRANCHE_BEGRIFFE_VEREIN,
  },
  stiftung: {
    art: "stiftung",
    seitenTitel: "Stiftung anlegen",
    kartenUeberschrift: "Angaben zur Stiftung",
    nameLabel: "Name der Stiftung",
    namePlatzhalter: "Musterstiftung",
    anschriftTitel: "Anschrift (Sitz)",
    // Nur der Vollständigkeit halber: `hatRegister: false` blendet das Paar aus,
    // solange nichts drinsteht. Steht doch etwas drin (Bestand, Umtragung einer
    // Stiftungs-gGmbH), gewinnt der Wert und braucht eine Beschriftung.
    registerNummerLabel: "Registernummer",
    registerLabel: "Register",
    registerPlatzhalter: "z. B. HRB 12345",
    hatRegister: false,
    branche: BRANCHE_BEGRIFFE_FIRMA,
  },
  genossenschaft: {
    art: "genossenschaft",
    seitenTitel: "Genossenschaft anlegen",
    kartenUeberschrift: "Angaben zur Genossenschaft",
    nameLabel: "Name der Genossenschaft",
    namePlatzhalter: "Mustergenossenschaft eG",
    anschriftTitel: "Anschrift (Sitz)",
    registerNummerLabel: "Genossenschaftsregisternummer",
    registerLabel: "Genossenschaftsregister",
    registerPlatzhalter: "z. B. GnR 123",
    hatRegister: true,
    branche: BRANCHE_BEGRIFFE_FIRMA,
  },
  gemeinschaft: {
    art: "gemeinschaft",
    seitenTitel: "Gemeinschaft anlegen",
    kartenUeberschrift: "Angaben zur Gemeinschaft",
    nameLabel: "Name der Gemeinschaft",
    namePlatzhalter: "Mustergemeinschaft",
    anschriftTitel: "Anschrift (Sitz)",
    registerNummerLabel: "Handelsregisternummer",
    registerLabel: "Handelsregister",
    registerPlatzhalter: "z. B. HRB 12345",
    hatRegister: true,
    branche: BRANCHE_BEGRIFFE_FIRMA,
  },
};

/**
 * Die eingetragene GbR steht seit dem MoPeG (01.01.2024) im
 * Gesellschaftsregister, nicht im Handelsregister — und soll dort genauso
 * abrufbar sein wie eine GmbH (T-329, Dirk 15.10.2026). Ihr `verbindungLabel`
 * ist „Unternehmen" und soll es bleiben (Verbindungsliste, Akte, Branche sind
 * die einer Firma); anders sind NUR die Registertexte. „GsR" ist die
 * Schreibweise, die das Suchformular des Registerportals führt.
 */
const GESELLSCHAFTSREGISTER: Pick<
  KoerperschaftTexte,
  "registerNummerLabel" | "registerLabel" | "registerPlatzhalter"
> = {
  registerNummerLabel: "Gesellschaftsregisternummer",
  registerLabel: "Gesellschaftsregister",
  registerPlatzhalter: "z. B. GsR 123",
};

/** Was von der Rechtsform gebraucht wird — ein `LegalFormOption` passt. */
export type RechtsformKennung = { kuerzel?: string | null; legalForm?: string | null };

/** Katalog: Kürzel „eGbR", Name „eingetragene GbR (eGbR)" (Groß/Klein schwankt). */
const istEingetrageneGbr = (rechtsform?: RechtsformKennung | null): boolean =>
  (rechtsform?.kuerzel ?? "").trim().toLowerCase() === "egbr" ||
  /\(egbr\)\s*$/i.test((rechtsform?.legalForm ?? "").trim());

/**
 * Texte zum Verbindungsbegriff einer Rechtsform. `verbindungLabel` ist ein frei
 * pflegbares Stammdatum (varchar) — unbekannt, leer oder „Unternehmen" ergibt
 * deshalb die Firmen-Texte. Die Rechtsform selbst entscheidet nur über das
 * Register der eGbR (siehe `GESELLSCHAFTSREGISTER`).
 */
export const koerperschaftTexte = (
  begriff?: string | null,
  rechtsform?: RechtsformKennung | null,
): KoerperschaftTexte => {
  const texte = NACH_BEGRIFF[(begriff ?? "").trim().toLowerCase()] ?? KOERPERSCHAFT_FIRMA;
  return istEingetrageneGbr(rechtsform) ? { ...texte, ...GESELLSCHAFTSREGISTER } : texte;
};

/**
 * Zeigt die Akte das Registerpaar (Nummer + Amtsgericht)?
 *
 * Ein gepflegter Wert schlägt die Regel: Blendete die Stiftung ihr Register
 * bedingungslos aus, verschwände eine im Bestand erfasste Nummer aus der
 * Ansicht, ohne aus der Datenbank zu verschwinden — sichtbar erst wieder für
 * den, der die Rechtsform ändert.
 */
export const zeigeRegisterFeld = (
  texte: KoerperschaftTexte,
  registerNummer?: string | null,
  registergericht?: string | null,
): boolean =>
  texte.hatRegister ||
  (registerNummer ?? "").trim() !== "" ||
  (registergericht ?? "").trim() !== "";

/**
 * Die vier Gemeinnützigkeits-Felder eines Kontakts — nur so viel, wie die
 * Sichtbarkeitsregel darunter lesen muss.
 */
export type GemeinnuetzigkeitWerte = {
  istGemeinnuetzig?: boolean | null;
  freistellungsbescheidBis?: string | null;
  zweckNrAO?: string | null;
  satzungszweck?: string | null;
};

/**
 * Steht an einem der vier Felder etwas? Ein ausdrückliches „Nein" zählt mit:
 * auch das ist eine Pflege, die sichtbar und rücknehmbar bleiben muss.
 */
const hatGemeinnuetzigkeitsWert = (werte?: GemeinnuetzigkeitWerte): boolean =>
  werte?.istGemeinnuetzig != null ||
  (werte?.freistellungsbescheidBis ?? "").trim() !== "" ||
  (werte?.zweckNrAO ?? "").trim() !== "" ||
  (werte?.satzungszweck ?? "").trim() !== "";

/**
 * Führt die Akte für diese Körperschaft den Gemeinnützigkeits-Block?
 *
 * Gemeinnützigkeit ist rechtsformübergreifend (§ 51 AO: jede Körperschaft
 * i. S. d. KStG, auch gGmbH, UG, AG). ERFASST wird sie in der Maske nur dort,
 * wo sie zum Regelfall gehört (PO 15.08.2026) — die gGmbH trägt die Zusage
 * bereits im Rechtsform-Namen.
 *
 * Ein gepflegter Wert schlägt die Regel, genau wie bei `zeigeRegisterFeld`:
 * Der Backfill der Migration 202608160005 setzt `istGemeinnuetzig = true` für
 * JEDE Rechtsform, deren Bezeichnung mit „gemeinn…" beginnt — also auch für
 * die „Gemeinnützige GmbH (gGmbH)", die als „Unternehmen" geführt wird. Ohne
 * diese Ausnahme stünde dort ein Flag in der Datenbank, das in der Akte weder
 * zu sehen noch zu korrigieren wäre.
 */
export const fuehrtGemeinnuetzigkeit = (
  texte: KoerperschaftTexte,
  werte?: GemeinnuetzigkeitWerte,
): boolean =>
  texte.art === "verein" ||
  texte.art === "stiftung" ||
  texte.art === "genossenschaft" ||
  hatGemeinnuetzigkeitsWert(werte);

/**
 * Zeigt der Block das Ende des Freistellungsbescheids?
 *
 * Regelfall ist „nur bei Ja": Ohne anerkannte Gemeinnützigkeit gibt es keinen
 * Bescheid, dessen Gültigkeit man erfassen könnte. Steht aber schon ein Datum
 * drin, gewinnt der Wert — sonst verschwände es beim Umschalten auf „Nein"
 * bloß aus der Maske, bliebe im Formularstand stehen und ginge beim Speichern
 * unverändert wieder mit: ein nicht gemeinnütziger Kontakt mit gültigem
 * Freistellungsbescheid, den niemand mehr löschen kann.
 */
export const zeigeFreistellungsbescheid = (
  istGemeinnuetzig?: boolean | null,
  freistellungsbescheidBis?: string | null,
): boolean => istGemeinnuetzig === true || (freistellungsbescheidBis ?? "").trim() !== "";
